import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, TableWrap } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Data cleaning: merge duplicate customers, products and suppliers; tidy how values are
 * written; archive what nobody uses any more. Merging never deletes anything.
 */

type Tab = 'duplicates' | 'format' | 'unused' | 'history';
type Match = 'exact' | 'words' | 'digits' | 'email';
type FAction = 'trim' | 'title' | 'upper' | 'lower' | 'phone';
const MATCHES: Match[] = ['words', 'exact', 'digits', 'email'];
const F_ACTIONS: FAction[] = ['trim', 'title', 'upper', 'lower', 'phone'];

interface ListInfo {
  model: string;
  label: string;
  fields: { name: string; label: string | null; unique: boolean }[];
  show: string[];
}
interface Rule {
  id: string;
  model: string;
  name: string;
  fields: { field: string; match: Match }[];
  active: boolean;
  scanned_at: string | null;
  open: number;
}
interface FormatRule {
  id: string;
  model: string;
  field: string;
  action: FAction;
}
interface Overview {
  lists: ListInfo[];
  rules: Rule[];
  formats: FormatRule[];
  open: number;
}
type Rec = Record<string, unknown> & { id: string; uses: number; created_at: string };
interface Dupes {
  rule: Rule;
  fields: string[];
  groups: { id: string; key: string; records: Rec[] }[];
}

const KNOWN_FIELDS = ['name', 'phone', 'email', 'sku', 'barcode', 'plu', 'tax_id', 'address', 'price', 'image'];
const fieldLabel = (name: string, label?: string | null) => (KNOWN_FIELDS.includes(name) ? t(`clean.f_${name}` as MessageKey) : (label ?? name));
const listLabel = (model: string) => t(`clean.list_${model.replace('.', '_')}` as MessageKey);
const LIST_ICON: Record<string, IconName> = { 'customer.customer': 'users', 'product.product': 'box', 'purchase.supplier': 'truck' };

export function Cleaning() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('cleaning.overview');
  const { busy, run } = useSave();
  const tabs: { key: Tab; icon: IconName }[] = [
    { key: 'duplicates', icon: 'merge' },
    { key: 'format', icon: 'text' },
    { key: 'unused', icon: 'archive' },
    { key: 'history', icon: 'history' },
  ];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'duplicates') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'duplicates') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const [version, setVersion] = useState(0);
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports cl-page">
        <div className="page-head">
          <div>
            <h1>{t('clean.title')}</h1>
            <p className="muted">{o ? (o.open ? tn('clean.subtitle_open', o.open) : t('clean.subtitle_clean')) : ' '}</p>
          </div>
          <Button
            variant="primary"
            icon="search"
            busy={busy}
            disabled={!o?.rules.length}
            onClick={() =>
              void run(async () => {
                const r = await action<{ found: number }>('cleaning.scan', {});
                await changed();
                return r;
              }, t('clean.scanned'))
            }
          >
            {t('clean.scan_all')}
          </Button>
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('clean.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`clean.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : !o.lists.length ? (
          <section className="panel">
            <EmptyState icon="sparkle" title={t('clean.no_lists')} body={t('clean.no_lists_body')} />
          </section>
        ) : tab === 'duplicates' ? (
          <DuplicatesTab key={version} o={o} onChange={changed} />
        ) : tab === 'format' ? (
          <FormatTab o={o} onChange={changed} />
        ) : tab === 'unused' ? (
          <UnusedTab o={o} />
        ) : (
          <HistoryTab key={version} />
        )}
      </div>
    </Shell>
  );
}

// ---- duplicates -------------------------------------------------------------------------

function DuplicatesTab({ o, onChange }: { o: Overview; onChange: () => Promise<void> }) {
  const [params, setParams] = useSearchParams();
  const ruleId = o.rules.some((r) => r.id === params.get('rule')) ? params.get('rule')! : (o.rules.find((r) => r.open)?.id ?? o.rules[0]?.id ?? null);
  const pick = (id: string) => {
    const p = new URLSearchParams(params);
    p.set('rule', id);
    setParams(p, { replace: true });
  };
  const [edit, setEdit] = useState<Rule | 'new' | null>(null);
  return (
    <div className="cl-dupes">
      <aside className="panel cl-rules">
        <div className="panel-head">
          <h2>{t('clean.rules')}</h2>
          <IconButton icon="plus" label={t('clean.add_rule')} onClick={() => setEdit('new')} />
        </div>
        {!o.rules.length ? (
          <p className="muted small">{t('clean.no_rules')}</p>
        ) : (
          o.lists.map((l) => {
            const mine = o.rules.filter((r) => r.model === l.model);
            if (!mine.length) return null;
            return (
              <div key={l.model} className="cl-rule-group">
                <h3>
                  <Icon name={LIST_ICON[l.model] ?? 'list'} size={14} /> {listLabel(l.model)}
                </h3>
                <ul>
                  {mine.map((r) => (
                    <li key={r.id}>
                      <button type="button" className={`cl-rule${r.id === ruleId ? ' on' : ''}${r.active ? '' : ' off'}`} aria-current={r.id === ruleId || undefined} onClick={() => pick(r.id)}>
                        <span>{r.name}</span>
                        {r.open ? <span className="cl-count">{r.open}</span> : <Icon name="check" size={14} />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </aside>
      <div className="cl-main">
        {ruleId ? (
          <RuleGroups key={ruleId} rule={o.rules.find((r) => r.id === ruleId)!} o={o} onEdit={(r) => setEdit(r)} onChange={onChange} />
        ) : (
          <section className="panel">
            <EmptyState icon="merge" title={t('clean.start_title')} body={t('clean.start_body')} action={<Button icon="plus" onClick={() => setEdit('new')}>{t('clean.add_rule')}</Button>} />
          </section>
        )}
      </div>
      {edit ? <RuleDialog rule={edit === 'new' ? null : edit} o={o} onClose={() => setEdit(null)} onSaved={onChange} /> : null}
    </div>
  );
}

function RuleGroups({ rule, o, onEdit, onChange }: { rule: Rule; o: Overview; onEdit: (r: Rule) => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Dupes>('cleaning.duplicates', { rule: rule.id });
  const { busy, run } = useSave();
  const list = o.lists.find((l) => l.model === rule.model)!;
  const describe = rule.fields.map((f) => t(`clean.rule_part_${f.match}` as MessageKey, { field: fieldLabel(f.field, list.fields.find((x) => x.name === f.field)?.label) })).join(t('clean.and'));
  return (
    <>
      <section className="panel cl-rule-head">
        <div>
          <h2>
            {listLabel(rule.model)} · {rule.name}
          </h2>
          <p className="muted small">
            {describe}
            {rule.scanned_at ? ` · ${t('clean.scanned_at', { when: dateTime(rule.scanned_at) })}` : ` · ${t('clean.never_scanned')}`}
          </p>
        </div>
        <span className="cl-rule-actions">
          <Button size="sm" icon="search" busy={busy} onClick={() => void run(async () => (await action('cleaning.scan', { rule: rule.id }), await reload(), await onChange()), t('clean.scanned'))}>
            {t('clean.scan')}
          </Button>
          <IconButton icon="edit" label={t('clean.edit_rule')} onClick={() => onEdit(rule)} />
        </span>
      </section>
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !data.groups.length ? (
        <section className="panel">
          <EmptyState icon="checkCircle" title={t('clean.no_groups')} body={rule.scanned_at ? t('clean.no_groups_body') : t('clean.scan_first')} />
        </section>
      ) : (
        <div className="cl-groups">
          {data.groups.map((g) => (
            <GroupCard key={g.id} g={g} fields={data.fields} list={list} onDone={async () => (await reload(), await onChange())} />
          ))}
        </div>
      )}
    </>
  );
}

const show = (v: unknown, field: string) => (v === null || v === undefined || v === '' ? '—' : field === 'price' ? money(Number(v)) : String(v));

function GroupCard({ g, fields, list, onDone }: { g: Dupes['groups'][number]; fields: string[]; list: ListInfo; onDone: () => Promise<void> }) {
  // Keep the one most used (then the oldest); merge all the others.
  const best = [...g.records].sort((a, b) => b.uses - a.uses || a.created_at.localeCompare(b.created_at))[0]!.id;
  const [keep, setKeep] = useState(best);
  const [merge, setMerge] = useState<string[]>(g.records.filter((r) => r.id !== best).map((r) => r.id));
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<{ moved: { list: string; count: number }[]; kept_final: number; filled: string[] } | null>(null);
  const { busy, run } = useSave();
  const choose = (id: string) => {
    setKeep(id);
    setMerge(g.records.filter((r) => r.id !== id).map((r) => r.id));
  };
  const kept = g.records.find((r) => r.id === keep)!;
  const name = String(kept.name ?? '');
  return (
    <section className="panel cl-group" aria-label={t('clean.group_label', { name })}>
      <div className="cl-group-head">
        <h3>{tn('clean.n_records', g.records.length)}</h3>
        <span className="cl-group-actions">
          <Button size="sm" variant="ghost" icon="x" busy={busy} onClick={() => void run(async () => (await action('cleaning.dismiss', { id: g.id }), await onDone()), t('clean.dismissed'))}>
            {t('clean.not_duplicates')}
          </Button>
          <Button size="sm" variant="primary" icon="merge" disabled={!merge.length} onClick={() => setConfirm(true)}>
            {t('clean.merge_n', { n: merge.length + 1 })}
          </Button>
        </span>
      </div>
      <TableWrap>
        <table className="data-table cl-compare">
          <thead>
            <tr>
              <th scope="col">{t('clean.keep')}</th>
              <th scope="col">{t('clean.merge')}</th>
              {fields.map((f) => (
                <th key={f} scope="col">
                  {fieldLabel(f, list.fields.find((x) => x.name === f)?.label)}
                </th>
              ))}
              <th scope="col" className="num">
                {t('clean.used_by')}
              </th>
              <th scope="col">{t('clean.created')}</th>
            </tr>
          </thead>
          <tbody>
            {g.records.map((r) => {
              const isKeep = r.id === keep;
              return (
                <tr key={r.id} className={isKeep ? 'cl-keep' : merge.includes(r.id) ? 'cl-merging' : undefined}>
                  <td className="cl-pick" data-label={t('clean.keep')}>
                    <input type="radio" name={`keep-${g.id}`} checked={isKeep} aria-label={t('clean.keep_this', { name: String(r.name ?? '') })} onChange={() => choose(r.id)} />
                  </td>
                  <td className="cl-pick" data-label={t('clean.merge')}>
                    <input
                      type="checkbox"
                      disabled={isKeep}
                      checked={!isKeep && merge.includes(r.id)}
                      aria-label={t('clean.merge_this', { name: String(r.name ?? '') })}
                      onChange={(e) => setMerge(e.target.checked ? [...merge, r.id] : merge.filter((x) => x !== r.id))}
                    />
                  </td>
                  {fields.map((f) => (
                    <td key={f} data-label={fieldLabel(f, list.fields.find((x) => x.name === f)?.label)} className={!isKeep && r[f] && !kept[f] ? 'cl-fills' : undefined} title={!isKeep && r[f] && !kept[f] ? t('clean.will_fill') : undefined}>
                      {show(r[f], f)}
                    </td>
                  ))}
                  <td className="num" data-label={t('clean.used_by')}>{r.uses}</td>
                  <td data-label={t('clean.created')}>{dayText(r.created_at.slice(0, 10))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>
      {result ? (
        <p className="callout cl-result" role="status">
          <Icon name="checkCircle" size={16} />
          <span>
            {result.moved.length ? t('clean.moved', { list: result.moved.map((m) => `${m.count} ${m.list}`).join(', ') }) : t('clean.moved_none')}
            {result.kept_final ? ` ${tn('clean.kept_final', result.kept_final)}` : ''}
          </span>
        </p>
      ) : null}
      {confirm ? (
        <Confirm
          title={t('clean.merge_title', { name })}
          body={
            <>
              <p>{tn('clean.merge_body', merge.length, { name })}</p>
              <ul className="cl-steps">
                <li>{t('clean.merge_step_move')}</li>
                <li>{t('clean.merge_step_fill')}</li>
                <li>{t('clean.merge_step_archive')}</li>
                <li>{t('clean.merge_step_final')}</li>
              </ul>
            </>
          }
          confirm={t('clean.merge_n', { n: merge.length + 1 })}
          busy={busy}
          onClose={() => setConfirm(false)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ moved: { list: string; count: number }[]; kept_final: number; filled: string[] }>('cleaning.merge', { group: g.id, keep, merge });
              setResult(r);
              setConfirm(false);
              window.setTimeout(() => void onDone(), 2500);
            }, t('clean.merged', { name }))
          }
        />
      ) : null}
    </section>
  );
}

function RuleDialog({ rule, o, onClose, onSaved }: { rule: Rule | null; o: Overview; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [model, setModel] = useState(rule?.model ?? o.lists[0]!.model);
  const list = o.lists.find((l) => l.model === model)!;
  const [name, setName] = useState(rule?.name ?? '');
  const [fields, setFields] = useState<{ field: string; match: Match }[]>(rule?.fields ?? [{ field: 'name', match: 'words' }]);
  const [drop, setDrop] = useState(false);
  const usable = list.fields.filter((f) => !['image'].includes(f.name));
  return (
    <Dialog
      title={rule ? t('clean.edit_rule') : t('clean.add_rule')}
      onClose={onClose}
      footer={
        <>
          {rule ? (
            <Button variant="ghost" icon="trash" className="cl-left" onClick={() => setDrop(true)}>
              {t('clean.delete_rule')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!name.trim() || !fields.length}
            onClick={() =>
              void run(async () => {
                await action('cleaning.save_rule', { id: rule?.id ?? null, model, name, fields });
                await onSaved();
                onClose();
              }, t('clean.rule_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('clean.list')} required>
          <select
            className="input"
            value={model}
            disabled={!!rule}
            onChange={(e) => {
              setModel(e.target.value);
              setFields([{ field: 'name', match: 'words' }]);
            }}
          >
            {o.lists.map((l) => (
              <option key={l.model} value={l.model}>
                {listLabel(l.model)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('clean.rule_name')} required>
          <input className="input" value={name} maxLength={80} placeholder={t('clean.rule_name_ph')} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      <h3 className="cl-sub">{t('clean.compare')}</h3>
      <p className="muted small">{t('clean.compare_lead')}</p>
      <ul className="cl-fields">
        {fields.map((f, i) => (
          <li key={i}>
            <select className="input" aria-label={t('clean.field_n', { n: i + 1 })} value={f.field} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
              {usable.map((x) => (
                <option key={x.name} value={x.name}>
                  {fieldLabel(x.name, x.label)}
                </option>
              ))}
            </select>
            <select className="input" aria-label={t('clean.how_n', { n: i + 1 })} value={f.match} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, match: e.target.value as Match } : x)))}>
              {MATCHES.map((m) => (
                <option key={m} value={m}>
                  {t(`clean.match_${m}` as MessageKey)}
                </option>
              ))}
            </select>
            <IconButton icon="x" label={t('clean.remove_field')} disabled={fields.length === 1} onClick={() => setFields(fields.filter((_, j) => j !== i))} />
          </li>
        ))}
      </ul>
      {fields.length < 4 ? (
        <Button size="sm" icon="plus" onClick={() => setFields([...fields, { field: usable.find((x) => !fields.some((f) => f.field === x.name))?.name ?? 'name', match: 'exact' }])}>
          {t('clean.add_field')}
        </Button>
      ) : null}
      {drop && rule ? (
        <Confirm
          title={t('clean.delete_rule_title', { name: rule.name })}
          body={t('clean.delete_rule_body')}
          confirm={t('clean.delete_rule')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('cleaning.delete_rule', { id: rule.id });
              await onSaved();
              onClose();
            }, t('clean.rule_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- formatting ---------------------------------------------------------------------------

function FormatTab({ o, onChange }: { o: Overview; onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [model, setModel] = useState(o.lists[0]!.model);
  const list = o.lists.find((l) => l.model === model)!;
  const [field, setField] = useState('name');
  const [act, setAct] = useState<FAction>('trim');
  const [preview, setPreview] = useState<FormatRule | null>(null);
  useEffect(() => setField(list.fields[0]?.name ?? 'name'), [model]);
  return (
    <div className="cl-format">
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>{t('clean.format_rules')}</h2>
            <p className="muted small">{t('clean.format_lead')}</p>
          </div>
        </div>
        {!o.formats.length ? (
          <p className="muted">{t('clean.no_formats')}</p>
        ) : (
          <ul className="cl-flist">
            {o.formats.map((f) => (
              <li key={f.id}>
                <span>
                  <strong>
                    {listLabel(f.model)} · {fieldLabel(f.field, o.lists.find((l) => l.model === f.model)?.fields.find((x) => x.name === f.field)?.label)}
                  </strong>
                  <span className="muted small">{t(`clean.act_${f.action}_hint` as MessageKey)}</span>
                </span>
                <span className="cl-flist-end">
                  <StatusBadge tone="info" icon="text" label={t(`clean.act_${f.action}` as MessageKey)} />
                  <Button size="sm" icon="eye" onClick={() => setPreview(f)}>
                    {t('clean.preview')}
                  </Button>
                  <IconButton icon="trash" label={t('clean.delete_format')} onClick={() => void run(async () => (await action('cleaning.delete_format', { id: f.id }), await onChange()))} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel">
        <h2>{t('clean.add_format')}</h2>
        <div className="cl-addformat">
          <Field label={t('clean.list')}>
            <select className="input" value={model} onChange={(e) => setModel(e.target.value)}>
              {o.lists.map((l) => (
                <option key={l.model} value={l.model}>
                  {listLabel(l.model)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('clean.field')}>
            <select className="input" value={field} onChange={(e) => setField(e.target.value)}>
              {list.fields.map((x) => (
                <option key={x.name} value={x.name}>
                  {fieldLabel(x.name, x.label)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('clean.action')} hint={t(`clean.act_${act}_hint` as MessageKey)}>
            <select className="input" value={act} onChange={(e) => setAct(e.target.value as FAction)}>
              {F_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {t(`clean.act_${a}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          <Button icon="plus" busy={busy} onClick={() => void run(async () => (await action('cleaning.save_format', { model, field, action: act }), await onChange()), t('clean.format_saved'))}>
            {t('clean.add')}
          </Button>
        </div>
      </section>
      {preview ? <FormatPreview rule={preview} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}

function FormatPreview({ rule, onClose }: { rule: FormatRule; onClose: () => void }) {
  const { data, reload } = useAction<{ total: number; changes: { id: string; name: string; before: string; after: string }[] }>('cleaning.preview_format', { id: rule.id });
  const { busy, run } = useSave();
  const [done, setDone] = useState<{ changed: number; clashes: string[] } | null>(null);
  return (
    <Dialog
      title={t('clean.preview_title', { list: listLabel(rule.model) })}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!data?.total}
            onClick={() =>
              void run(async () => {
                setDone(await action('cleaning.apply_format', { id: rule.id }));
                await reload();
              })
            }
          >
            {data ? tn('clean.apply_n', data.total) : t('clean.apply')}
          </Button>
        </>
      }
    >
      {done ? (
        <p className="callout cl-result" role="status">
          <Icon name="checkCircle" size={16} />
          <span>
            {tn('clean.applied', done.changed)}
            {done.clashes.length ? ` ${t('clean.clashes', { names: done.clashes.join(', ') })}` : ''}
          </span>
        </p>
      ) : null}
      {!data ? (
        <Skeleton h={160} />
      ) : (
        <>
          {data.total > data.changes.length ? <p className="muted small">{t('clean.first_n', { n: data.changes.length, total: data.total })}</p> : null}
          <Table
            head={[t('clean.record'), t('clean.before'), t('clean.after')]}
            left={[1, 2]}
            empty={t('clean.nothing_to_tidy')}
            rows={data.changes.map((c) => ({
              key: c.id,
              cells: [c.name, <span className="cl-before">{c.before}</span>, <strong className="cl-after">{c.after}</strong>],
            }))}
          />
        </>
      )}
    </Dialog>
  );
}

// ---- unused -------------------------------------------------------------------------------

function UnusedTab({ o }: { o: Overview }) {
  const { busy, run } = useSave();
  const archivable = o.lists;
  const [model, setModel] = useState(archivable[0]!.model);
  const [days, setDays] = useState(365);
  const [data, setData] = useState<{ total: number; records: { id: string; name: string; created_at: string; updated_at: string }[] } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const look = () =>
    run(async () => {
      const r = await action<NonNullable<typeof data>>('cleaning.preview_unused', { model, days });
      setData(r);
      setPicked(new Set(r.records.map((x) => x.id)));
    });
  useEffect(() => setData(null), [model, days]);
  const all = useMemo(() => !!data && data.records.length > 0 && data.records.every((r) => picked.has(r.id)), [data, picked]);
  return (
    <section className="panel cl-unused">
      <div className="panel-head">
        <div>
          <h2>{t('clean.unused_title')}</h2>
          <p className="muted small">{t('clean.unused_lead')}</p>
        </div>
      </div>
      <div className="cl-addformat">
        <Field label={t('clean.list')}>
          <select className="input" value={model} onChange={(e) => setModel(e.target.value)}>
            {archivable.map((l) => (
              <option key={l.model} value={l.model}>
                {listLabel(l.model)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('clean.not_changed_for')}>
          <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[90, 180, 365, 730, 1095].map((d) => (
              <option key={d} value={d}>
                {d >= 365 ? tn('clean.years', d / 365) : tn('clean.months', d / 30)}
              </option>
            ))}
          </select>
        </Field>
        <Button icon="search" busy={busy && !data} onClick={() => void look()}>
          {t('clean.find_unused')}
        </Button>
      </div>
      {data ? (
        !data.records.length ? (
          <EmptyState icon="checkCircle" title={t('clean.none_unused')} body={t('clean.none_unused_body')} />
        ) : (
          <>
            <div className="cl-unused-bar">
              <label className="cl-all">
                <input type="checkbox" checked={all} onChange={(e) => setPicked(e.target.checked ? new Set(data.records.map((r) => r.id)) : new Set())} />
                {t('clean.select_all')}
              </label>
              <span className="muted small">{data.total > data.records.length ? t('clean.first_n', { n: data.records.length, total: data.total }) : tn('clean.found_unused', data.total)}</span>
              <span className="cl-spacer" />
              <Button variant="danger" icon="archive" disabled={!picked.size} onClick={() => setConfirm(true)}>
                {tn('clean.archive_n', picked.size)}
              </Button>
            </div>
            <ul className="cl-unused-list">
              {data.records.map((r) => (
                <li key={r.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={picked.has(r.id)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                        setPicked(next);
                      }}
                    />
                    <span className="cl-unused-name">{r.name}</span>
                    <span className="muted small">{t('clean.last_changed', { day: dayText(r.updated_at.slice(0, 10)) })}</span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
      {confirm && data ? (
        <Confirm
          title={tn('clean.archive_title', picked.size)}
          body={t('clean.archive_body')}
          confirm={tn('clean.archive_n', picked.size)}
          danger
          busy={busy}
          onClose={() => setConfirm(false)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ archived: number }>('cleaning.archive_unused', { model, days, ids: [...picked] });
              setConfirm(false);
              setData(null);
              return r;
            }, t('clean.archived'))
          }
        />
      ) : null}
    </section>
  );
}

// ---- history ------------------------------------------------------------------------------

function HistoryTab() {
  const { data } = useAction<
    { id: string; created_at: string; list: string; model: string; kept_name: string; merged: { name: string }[]; moved: { list: string; count: number }[]; kept_final: number; by_name: string | null }[]
  >('cleaning.history');
  if (!data) return <Skeleton h={200} r="var(--radius-lg)" />;
  return (
    <section className="panel">
      <Table
        head={[t('clean.when'), t('clean.list'), t('clean.kept'), t('clean.merged_in'), t('clean.moved_col'), t('clean.by')]}
        phoneHide={[1, 4, 5]}
        empty={t('clean.no_history')}
        rows={data.map((m) => ({
          key: m.id,
          cells: [
            dateTime(m.created_at),
            listLabel(m.model),
            <strong>{m.kept_name}</strong>,
            m.merged.map((x) => x.name).join(', '),
            <>
              {m.moved.map((x) => `${x.count} ${x.list}`).join(', ') || '—'}
              {m.kept_final ? <span className="muted small"> · {tn('clean.final_short', m.kept_final)}</span> : null}
            </>,
            m.by_name ?? '—',
          ],
        }))}
      />
    </section>
  );
}
